/**
 * Audit Dashboard — Cycle 10. admin_override 시계열 chart.
 * Cycle 19 — byAction CSV export 버튼 추가.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  getAdminOverrideStats, getLlmUsage,
  type AdminOverrideStats, type LlmUsageResponse,
} from '../../api/systemAdmin';
// Cycle 43 — lang-aware date locale
import { getNumberLocale, subscribeSharedLang, getSharedLang, formatDateTime } from '../../lib/base-i18n';
import '../AdminConsolePage/AdminConsolePage.css';

// Cycle 19 — CSV export helper. RFC 4180 준수: quote 내 quote 는 두 번, value 에 quote/comma/newline 있으면 quote 로 감쌈.
export function toCsv(rows: Array<Record<string, string | number>>): string {
  if (rows.length === 0) return '';
  const first = rows[0];
  if (!first) return '';
  const headers = Object.keys(first);
  const esc = (v: string | number): string => {
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h] ?? '')).join(','))];
  return lines.join('\n');
}

export function AuditDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [stats, setStats] = useState<AdminOverrideStats | null>(null);
  const [usage, setUsage] = useState<LlmUsageResponse | null>(null);
  const [days, setDays] = useState(7);
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Cycle 43 — lang 변경 시 re-render
  const [, setLangBump] = useState(getSharedLang());
  useEffect(() => subscribeSharedLang(() => setLangBump(getSharedLang())), []);

  useEffect(() => {
    setError(null);
    const opts = institutionFilter ? { days, institutionId: institutionFilter } : { days };
    Promise.all([getAdminOverrideStats(opts), getLlmUsage()])
      .then(([s, u]) => { setStats(s); setUsage(u); })
      .catch((e) => setError(e?.response?.data?.message ?? '불러오기 실패'));
  }, [days, institutionFilter]);

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <div className="admin-console admin-console--denied"><h2>접근 권한이 없습니다</h2></div>;
  }
  const max = stats ? Math.max(...stats.bySeriesDay.map((d) => d.count), 1) : 1;

  // Cycle 20 — CSV 에 bySeriesDay 포함 옵션
  const [csvIncludeSeries, setCsvIncludeSeries] = useState(false);

  // Cycle 19/20 — byAction + recent (+ bySeriesDay 옵션) CSV 다운로드. Blob → object URL → anchor click.
  function downloadCsv() {
    if (!stats) return;
    const byActionRows = Object.entries(stats.byAction)
      .sort((a, b) => b[1] - a[1])
      .map(([action, count]) => ({ section: 'byAction', day: '', action, count, ts: '', tenant: '', actor: '' }));
    const recentRows = stats.recent.map((r) => ({
      section: 'recent', day: '', action: r.action, count: 1,
      ts: r.ts, tenant: r.institutionId, actor: r.actorId ?? '',
    }));
    const seriesRows = csvIncludeSeries
      ? stats.bySeriesDay.map((d) => ({
          section: 'bySeriesDay', day: d.day, action: '', count: d.count, ts: '', tenant: '', actor: '',
        }))
      : [];
    const csv = toCsv([...byActionRows, ...recentRows, ...seriesRows]);
    // BOM (excel ko encoding) — utf-8 이지만 excel 호환 BOM
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const filter = institutionFilter ? `_inst${institutionFilter}` : '';
    const series = csvIncludeSeries ? '_series' : '';
    const filename = `audit_${days}d${filter}${series}_${today}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return (
    <div className="admin-console">
      <aside className="admin-console__sidebar">
        <Link to="/system-admin" className="admin-console__btn" style={{ width: '100%', textDecoration: 'none', textAlign: 'center', display: 'block' }}>
          ← 테넌트 목록
        </Link>
      </aside>
      <main className="admin-console__content">
        <h2 className="admin-console__heading">관리자 활동 대시보드</h2>
        <p className="admin-console__subheading">admin_override 이벤트 (LLM CRUD / ServiceModule toggle / user role / freeze / status change) 집계.</p>

        {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="window">기간:</label>
          {/* no-native-select-exception: AuditDashboard 의 date range 는 4 옵션 정적 list — Combobox 의 search/dynamic options 가치 없음. cycle 40 AdminConsolePage 의 정책과 같은 본질 (작은 정적 select 는 native 적절). */}
          <select id="window" value={days} onChange={(e) => setDays(Number(e.target.value))} className="admin-console__input" style={{ width: 'auto' }}>
            <option value={1}>1일</option>
            <option value={7}>7일</option>
            <option value={30}>30일</option>
            <option value={90}>90일</option>
          </select>

          <label htmlFor="instFilter" style={{ marginLeft: '0.5rem' }}>tenant:</label>
          <input
            id="instFilter"
            type="text"
            inputMode="numeric"
            placeholder="전체 (또는 ID)"
            className="admin-console__input"
            style={{ width: '150px' }}
            value={institutionFilter}
            onChange={(e) => setInstitutionFilter(e.target.value.replace(/\D/g, ''))}
          />

          <button
            type="button"
            className="admin-console__btn"
            onClick={downloadCsv}
            disabled={!stats}
            style={{ marginLeft: '0.5rem' }}
            aria-label="byAction + recent CSV 다운로드"
          >
            CSV 다운로드
          </button>
          <label style={{ fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <input
              type="checkbox"
              checked={csvIncludeSeries}
              onChange={(e) => setCsvIncludeSeries(e.target.checked)}
              aria-label="bySeriesDay 포함"
            />
            <span>일자별 포함</span>
          </label>

          <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            총 {stats?.totalEvents ?? 0} 이벤트
          </span>
        </div>

        {stats && (
          <>
            <section style={{ marginBottom: '2rem' }}>
              <h3>일자별 분포</h3>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '140px', borderBottom: '1px solid var(--color-border, #e5e7eb)', padding: '0 0.5rem' }} aria-label="일자별 이벤트 분포">
                {stats.bySeriesDay.map((d) => (
                  <div key={d.day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div
                      style={{
                        width: '100%',
                        height: `${(d.count / max) * 100}%`,
                        background: 'var(--color-brand, #4f46e5)',
                        borderRadius: '4px 4px 0 0',
                        minHeight: '2px',
                      }}
                      title={`${d.day}: ${d.count}`}
                    />
                    <span style={{ fontSize: '0.625rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                      {d.day.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h3>action 별 카운트</h3>
              <table className="admin-console__table">
                <thead><tr><th>action</th><th>count</th></tr></thead>
                <tbody>
                  {Object.entries(stats.byAction).sort((a, b) => b[1] - a[1]).map(([action, count]) => (
                    <tr key={action}>
                      <td><code style={{ fontSize: '0.8125rem' }}>{action}</code></td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {usage && (
              <section style={{ marginBottom: '2rem' }}>
                <h3>LLM 토큰 사용량 ({usage.date})</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  총 {usage.totalTokensUsed.toLocaleString(getNumberLocale())} 토큰 · {usage.totalTenants} 테넌트 ·
                  quota 임박 (≥90%) {usage.overQuotaCount} 곳
                </p>
                {/*
                  ★ 이 숫자가 «무엇의 합»인지 적는다 — 안 적으면 «전부»로 읽힌다.
                    provider 가 토큰 수를 안 알려 준 호출은 0 으로 지어내지 않고 아예 안 센다.
                */}
                <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                  AI 채팅·AI 패널·오피스 편집기 AI 가 실제로 쓴 토큰의 합입니다 — provider 가 토큰 수를
                  알려 준 호출만 셉니다(AI 회의록은 아직 세지 않습니다).
                </p>
                <table className="admin-console__table">
                  <thead><tr><th>회사</th><th>오늘 사용</th><th>한도</th><th>사용률</th></tr></thead>
                  <tbody>
                    {usage.items.slice(0, 15).map((u) => (
                      <tr key={u.institutionId}>
                        <td>
                          <Link to={`/system-admin/tenants/${u.institutionId}`}>{u.name}</Link>
                          <br />
                          <code style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                            {u.domain}
                          </code>
                        </td>
                        <td>{u.usedToday.toLocaleString(getNumberLocale())}</td>
                        <td>{u.limit ? Number(u.limit).toLocaleString(getNumberLocale()) : '제한 없음'}</td>
                        <td>
                          {u.utilizationPct !== null
                            ? <span style={{ color: u.utilizationPct >= 90 ? 'var(--color-danger, #b91c1c)' : 'inherit', fontWeight: u.utilizationPct >= 90 ? 600 : 400 }}>{u.utilizationPct}%</span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <section>
              <h3>최근 이벤트 (20건)</h3>
              <table className="admin-console__table">
                <thead><tr><th>시각</th><th>action</th><th>tenant</th><th>actor</th></tr></thead>
                <tbody>
                  {stats.recent.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: '0.8125rem' }}>{formatDateTime(r.ts)}</td>
                      <td><code style={{ fontSize: '0.8125rem' }}>{r.action}</code></td>
                      <td><Link to={`/system-admin/tenants/${r.institutionId}`}>{r.institutionId}</Link></td>
                      <td>{r.actorId ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
